import React, { useMemo, Fragment } from 'react';
import { ScissorsIcon } from 'lucide-react';
import cvData from '../../data/cv';
import { Masthead } from './Masthead';
import { Rubric } from './Rubric';
import { ExperienceEntry } from './ExperienceEntry';
import { ProfileBlock, CapabilitiesBlock, EducationBlock } from './Aside';
import { EarlierExperience, type CondensedRole } from './EarlierExperience';
import { Page } from './Page';
import { periodSortKey, condenseRole } from './helpers';
import { useMeasuredPages, type Block } from './useMeasuredPages';
// A4 content area height: 297mm − 28mm margins ≈ 1017px @96dpi, minus the
// page footer (~46px) so packed content never overflows into it.
const PAGE_HEIGHT_PX = 928;
const CONTENT_WIDTH_PX = 680;
export function CVSheet({
  compact = false
}: {
  compact?: boolean;
}) {
  const {
    experience,
    earlierExperience,
    meta
  } = cvData;
  const S = meta.sections;
  // Condensed mode: keep exactly 5 roles in full detail (proven to pack into a
  // 3-page document). Newer roles take the top detailed slots; the oldest ones
  // spill into the condensed "Earlier Experience" list so the page count holds
  // steady as recent roles are added.
  const detailedCount = compact ? 5 : meta.detailedRoleCount;
  const roles = useMemo(() => [...experience].sort((a, b) => periodSortKey(b.period) - periodSortKey(a.period)), [experience]);
  const detailed = useMemo(() => roles.slice(0, detailedCount), [roles, detailedCount]);
  const condensed: CondensedRole[] = useMemo(() => [...roles.slice(detailedCount).map(condenseRole), ...earlierExperience], [roles, detailedCount, earlierExperience]);
  const blocks: Block[] = useMemo(() => {
    const list: Block[] = [];
    const gap = compact ? 'mt-4' : 'mt-11';
    // Masthead is its own block. The Profile + Capabilities intro band is a
    // SEPARATE block so the packer can flow them independently instead of
    // treating the whole (near-full-page) header as one atomic unit — that
    // stranding was forcing extra pages.
    list.push({
      key: 'masthead',
      node: <Masthead compact={compact} data-id="element-60" data-mp="1wunct4fr73tm" />
    });
    list.push({
      key: 'intro-band',
      node: <div className={`${compact ? 'gap-x-8 gap-y-4' : 'gap-x-12 gap-y-10'} grid grid-cols-12`} data-id="element-61" data-mp="2brw5c1gsmdmx">
          <div className={compact ? 'col-span-5' : 'col-span-7'} data-id="element-62" data-mp="26q6v2o1ea3zw">
            <ProfileBlock compact={compact} data-id="element-63" data-mp="2kqrdvk7b7zte" />
          </div>
          <div className={compact ? 'col-span-7' : 'col-span-5'} data-id="element-64" data-mp="2kp6jpvowqiw2">
            <CapabilitiesBlock compact={compact} data-id="element-65" data-mp="2frwvukh62fac" />
          </div>
        </div>
    });
    // The Experience heading is folded into the first role block so the heading
    // can never orphan from its roles nor strand a near-empty page (the failure
    // mode of a standalone keepWithNext heading after the tall intro band).
    detailed.forEach((item, i) => {
      list.push({
        key: `role-${i}`,
        node: <>
            {i === 0 && <div className={`${gap} mb-4`} data-id="element-66" data-mp="3i4hgmm9psup5">
                <Rubric index={S.experience.index} title={S.experience.title} count={`${detailed.length} recent roles`} compact={compact} data-id="element-67" data-mp="3i2mux5qmcyk0" />
              </div>}
            <ExperienceEntry item={item} isLast={i === detailed.length - 1} compact={compact} data-id="element-68" data-mp="3e232ugkwkl4q" />
          </>
      });
    });
    if (!compact) {
      // Full mode: each earlier role as its own condensed row.
      list.push({
        key: 'earlier-head',
        keepWithNext: true,
        node: <div className={gap} data-id="element-69" data-mp="2uevky8sjwxlw">
            <Rubric index={S.earlier.index} title={S.earlier.title} count={`${condensed.length} roles`} data-id="element-70" data-mp="2ug6oc7i343ch" />
          </div>
      });
      condensed.forEach((r, i) => {
        list.push({
          key: `cond-${i}`,
          node: <EarlierExperience roles={[r]} data-id="element-71" data-mp="3ns4pejnhi1j4" />
        });
      });
    } else if (condensed.length > 0) {
      // Condensed mode: Earlier Experience (left) + Education (right) share one
      // two-column block. It flows into whatever space remains after the
      // detailed roles (packing keeps it intact), so an added recent role uses
      // up free space instead of forcing an extra page.
      list.push({
        key: 'tail',
        node: <div className={gap} data-id="element-72" data-mp="34zajsgbr3lrk">
            <div className="grid grid-cols-12 gap-x-10" data-id="element-73" data-mp="302k4vwbomw7q">
              <div className="col-span-8" data-id="element-74" data-mp="300zbwb50nlbz">
                <Rubric index={S.earlier.index} title={S.earlier.title} count={`${condensed.length} roles`} compact={compact} data-id="element-75" data-mp="2w070pccl36d2" />
                <ul className="divide-y divide-[var(--line)]" data-id="element-76" data-mp="u9lrdy47qw7a">
                  {condensed.map((r, i) => <li key={i} className="grid grid-cols-[92px_1fr] items-baseline gap-3 py-1.5 first:pt-0" data-id="element-77" data-mp="pbkc7o9wrh13" data-mpi={i}>
                      <span className="whitespace-nowrap font-mono text-[9px] tabular-nums tracking-wide text-[var(--ink-faint)]" data-id="element-78" data-mp="11sbig9j5q4hs">
                        {r.period}
                      </span>
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5" data-id="element-79" data-mp="1bj68lozixo8i">
                        <span className="font-display text-[12px] font-medium leading-snug text-[var(--ink)]" data-id="element-80" data-mp="dv5ums182xkg">
                          {r.company}
                        </span>
                        <span className="font-mono text-[8.5px] uppercase tracking-[0.08em] text-[var(--accent)]" data-id="element-81" data-mp="ivtvs6j60use">
                          {r.role}
                        </span>
                      </div>
                    </li>)}
                </ul>
              </div>
              <div className="col-span-4" data-id="element-82" data-mp="1zabmhylwm6g6">
                <EducationBlock compact={compact} data-id="element-83" data-mp="2392nwhxeka93" />
              </div>
            </div>
          </div>
      });
      return list;
    }
    list.push({
      key: 'edu-head',
      keepWithNext: true,
      node: <div className={gap} data-id="element-84" data-mp="3vac3w08vawrm" />
    });
    list.push({
      key: 'edu',
      node: <EducationBlock compact={compact} data-id="element-85" data-mp="d6afaziorzgb" />
    });
    return list;
  }, [detailed, condensed, S, compact]);
  const {
    setRef,
    pages
  } = useMeasuredPages(blocks, PAGE_HEIGHT_PX, String(compact));
  // Hidden measuring layer. offsetHeight is transform-independent, so measuring
  // works even though CVSheet renders inside the zoom/pan transform. Rendered as
  // a plain absolutely-positioned sibling (no portal needed).
  const measuringLayer = <div aria-hidden="true" className="no-print" style={{
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    left: '-99999px',
    top: 0,
    width: `${CONTENT_WIDTH_PX}px`
  }} data-id="element-86" data-mp="3qedkkr2ec4y2">
      {blocks.map(b => <div key={b.key} ref={setRef(b.key)} className="mb-5" data-id="element-87" data-mp="2suu09bdztrxw">
          {b.node}
        </div>)}
    </div>;
  const total = pages.length;
  return <div className="relative flex flex-col items-center" data-id="element-88" data-mp="1d5uih4ejjqgq">
      {measuringLayer}
      {pages.map((pageBlocks, pageIdx) => <Fragment key={pageIdx} data-id="element-89" data-mp="zfr0ipmp2ix3" data-mpi={pageIdx}>
          {/* On-screen page-break divider (never printed). */}
          {pageIdx > 0 && <div className="no-print flex w-[210mm] max-w-full items-center gap-3 py-5" aria-hidden="true" data-id="element-90" data-mp="14awgz8xbv5ds">
              <span className="h-px flex-1 bg-[var(--line-strong)]" data-id="element-91" data-mp="1w3f5s71nqgz8" />
              <span className="flex items-center gap-1.5 rounded-full border border-[var(--line-strong)] bg-white/70 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--ink-faint)]" data-id="element-92" data-mp="1i4xiubjkt8id">
                <ScissorsIcon className="h-3 w-3" data-id="element-93" data-mp="6oz8838r2ef1" />
                Page {pageIdx + 1} of {total}
              </span>
              <span className="h-px flex-1 bg-[var(--line-strong)]" data-id="element-94" data-mp="bpmvcdtuyb3z" />
            </div>}
          <Page index={pageIdx + 1} total={total} data-id="element-95" data-mp="13d7rzpx637vm">
            <div className={compact ? 'space-y-3' : 'space-y-5'} data-id="element-96" data-mp="139tqejwbv2p8">
              {pageBlocks.map(b => <div key={b.key} data-id="element-97" data-mp="te0g7762zlam">{b.node}</div>)}
            </div>
          </Page>
        </Fragment>)}
    </div>;
}